import * as React from 'react';
import {
  type Type,
  getSystem,
  Types,
  Plugins,
  matchType,
} from '@packages/systems/wfdl';
import {TEmptyRecord} from '@packages/systems/wfdl-ast';
import {defer} from '@packages/utilities/lazy';
import {weakMemo} from '@packages/utilities/memo';
import {emptyArray} from '@packages/utilities/fp/utils';
import type {
  TreeViewNode,
  TreeViewNodeList,
} from '@packages/systems/design-system/TreeView';

export type TreeNode = {
  id: string;
  label: null | React.ReactNode;
  childrenCount: number;
  icon: Plugins.QualifiedPluginIcon;
  dataAutomationId?: string;
  type: Type;
};

export type TreeNodes = TreeViewNodeList<'children', TreeNode>;

export function typeToTreeNodes(
  type: Type,
  plugins: Plugins.PluginsType
): TreeNodes {
  return typeToTreeNodes_memo(plugins)(type);
}

const typeToTreeNodes_memo = weakMemo(function typeToTreeNodes_memoPlugins(
  plugins: Plugins.PluginsType
) {
  return weakMemo(function typeToTreeNodes_memoType(type: Type) {
    const idPath: Array<any | number> = [];
    const treeNodes: Array<TreeViewNode<'children', TreeNode>> = [];
    const label = null;
    return _typeToTreeNodes(type)(label, idPath, plugins, treeNodes);
  });
});

const noNodeChildren = defer(() => emptyArray);
const defaultCallback =
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.

  (_, type: Type) =>
    (
      label: null | string,
      idPath: Array<any | number>,
      plugins: Plugins.PluginsType,
      treeNodes: Array<TreeViewNode<'children', TreeNode>>
    ) => {
      label = getSystem(type)?.label || label || '';

      treeNodes.push([
        {
          id: JSON.stringify(idPath),
          label,
          type,
          childrenCount: 0,
          icon: Types.getIcon(type, plugins),
        },
        noNodeChildren,
      ]);
      return treeNodes;
    };

const _typeToTreeNodes = matchType({
  Element: defaultCallback,
  Generic: defaultCallback,
  RowCons: defaultCallback,
  RowEmpty: defaultCallback,
  TypeReference: defaultCallback,
  Rigid: defaultCallback,
  Unknown: defaultCallback,

  TypeApplication:
    (tyApp, type) =>
    (
      label: null | string,
      idPath: Array<any | number>,
      plugins: Plugins.PluginsType,
      treeNodes: Array<TreeViewNode<'children', TreeNode>>
    ) => {
      label = getSystem(type)?.label || label;

      const fields = Types.getRecordFields(type);

      if (fields) {
        const fieldEntries = Object.entries(fields);

        if (label == null) {
          treeNodes.push(
            ...fieldEntries.flatMap(([fieldName, field]: [any, any], i) =>
              _typeToTreeNodes(field)(fieldName, idPath.concat(i), plugins, [])
            )
          );
        } else {
          treeNodes.push([
            {
              id: JSON.stringify(idPath),
              label,
              type,
              childrenCount: fieldEntries.length,
              icon: Types.getIcon(type, plugins),
            },
            defer(() => [
              [
                'children',
                fieldEntries.flatMap(([fieldName, field]: [any, any], i) =>
                  _typeToTreeNodes(field)(
                    fieldName,
                    idPath.concat(i),
                    plugins,
                    []
                  )
                ),
              ],
            ]),
          ]);
        }

        return treeNodes;
      }

      const unionValues = Types.getUnionValues(type);
      if (unionValues) {
        const unionEntries = Object.entries(unionValues);

        treeNodes.push([
          {
            id: JSON.stringify(idPath),
            label,
            type,
            childrenCount: unionEntries.length,
            icon: Types.getIcon(type, plugins),
          },
          defer(() => [
            [
              'children',
              unionEntries.flatMap(([tag, val]: [any, any], i) =>
                _typeToTreeNodes(val)(tag, idPath.concat(i), plugins, [])
              ),
            ],
          ]),
        ]);

        return treeNodes;
      }

      const enumValues = Types.getEnumValues(type);
      if (enumValues) {
        const enumEntries = Object.entries(enumValues);
        treeNodes.push([
          {
            id: JSON.stringify(idPath),
            label,
            type,
            childrenCount: enumEntries.length,
            icon: Types.getIcon(type, plugins),
          },
          defer(() => [
            [
              'children',
              // @ts-expect-error - TS2345 - Argument of type '([enumName]: [any], i: number) => [{ id: any[]; label: any; childrenCount: number; icon: QualifiedPluginIcon; }, Lazy<any[]>]' is not assignable to parameter of type '(value: [string, string], index: number, array: [string, string][]) => [{ id: any[]; label: any; childrenCount: number; icon: QualifiedPluginIcon; }, Lazy<any[]>]'.
              enumEntries.map(([enumName]: [any], i) => [
                {
                  id: idPath.concat(i),
                  label: enumName,
                  childrenCount: 0,
                  icon: Types.getIcon(TEmptyRecord, plugins),
                },
                noNodeChildren,
              ]),
            ],
          ]),
        ]);

        return treeNodes;
      }

      treeNodes.push([
        {
          id: JSON.stringify(idPath),
          label,
          type,
          childrenCount: 0,
          icon: Types.getIcon(type, plugins),
        },
        noNodeChildren,
      ]);

      return treeNodes;
    },

  TypeConstructor:
    (tyCon, type) =>
    (
      label: null | string,
      idPath: Array<any | number>,
      plugins: Plugins.PluginsType,
      treeNodes: Array<TreeViewNode<'children', TreeNode>>
    ) => {
      label = getSystem(type)?.label || label;
      treeNodes.push([
        {
          id: JSON.stringify(idPath),
          label,
          type,
          childrenCount: 0,
          icon: Types.getIcon(type, plugins),
        },
        noNodeChildren,
      ]);

      return treeNodes;
    },
});
